/** @jsxImportSource @emotion/react */
"use client"; // error components must be Client Components

import Bugsnag from "@bugsnag/js";
import type { Interpolation } from "@emotion/react";
import {
  Button,
  numericPercentages,
  numericPixels,
  spacing,
  Text,
} from "@packages/ui";
import { useEffect } from "react";

const errorContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  height: numericPixels[360],
  justifyContent: "center",
  maxHeight: numericPercentages[100],
};

const titleStyles: Interpolation = {
  marginBottom: spacing.xl,
};

// Thrown by webpack's __webpack_require__ when it resolves a moduleId that
// isn't in __webpack_modules__ — happens when a client still holds JS
// referencing chunks from a build that a new deploy has since replaced.
// reset() re-renders the segment but can't fetch the missing module, so the
// error just recurs; only a hard reload picks up the current build's assets.
//
// The message wording (and the minified identifier before `.call`) differs
// by JS engine, so match the shape rather than literal text:
//  - V8 (Chrome/Node): Cannot read properties of undefined (reading 'call')
//  - JSC (Safari):     undefined is not an object (evaluating 'i[e].call')
const STALE_CHUNK_ERROR_PATTERN =
  /Loading chunk|ChunkLoadError|Cannot read properties of undefined \(reading 'call'\)|undefined is not an object \(evaluating '[\w$[\].]*\.call'\)/;
const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload-at";
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

// sessionStorage can throw (Safari private mode, sandboxed iframes, storage
// disabled) and a corrupted stored value makes Number(...) NaN — either
// would otherwise block the reload permanently, so fail open on both.
const getLastStaleChunkReloadAt = () => {
  try {
    return Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
};

const markStaleChunkReloadAttempt = () => {
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // Nothing to persist — the reload still proceeds without the cooldown.
  }
};

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Bugsnag.notify(error);

    if (STALE_CHUNK_ERROR_PATTERN.test(error.message)) {
      const lastReload = getLastStaleChunkReloadAt();
      if (Date.now() - lastReload > STALE_CHUNK_RELOAD_COOLDOWN_MS) {
        markStaleChunkReloadAttempt();
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div css={errorContainerStyles}>
      <Text size="m" type="headline" css={titleStyles}>
        Something went wrong!
      </Text>
      <Button
        // attempt to recover by trying to re-render the segment
        onClick={() => reset()}
      >
        Try again
      </Button>
    </div>
  );
}
